import { z } from "zod";

import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import {
  Appointment,
  AppointmentIntegrationNamespace,
  AppointmentTarget,
  EhrAppointmentIdNamespace,
  appointmentTargetSchema,
} from "@/src/models/appointment";
import { isoDateTimeSchema } from "@/src/models/common-schema";
import { Group } from "@/src/models/group";

const COBALT_ECW_IDENTITY_SUBDOMAINS = new Set<ConfigSubdomain>([
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.ENT_PARTNERS_SCI,
]);

const cobaltAppointmentIdentifierSchema = z.string().trim().min(1);

const cobaltAppointmentCreatedWebhookDataSchema = z.object({
  appointment_id: cobaltAppointmentIdentifierSchema,
  emr_appointment_id: cobaltAppointmentIdentifierSchema,
});

export function normalizeCobaltAppointmentIdentifier(value: unknown): string | null {
  const parsed = cobaltAppointmentIdentifierSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
}

export function supportsCobaltEcwAppointmentIdentity(group: Group): boolean {
  return COBALT_ECW_IDENTITY_SUBDOMAINS.has(getGroupConfigSubdomain(group) as ConfigSubdomain);
}

export function getCobaltPatientEncounterEhrNamespace(group: Group): EhrAppointmentIdNamespace | null {
  return supportsCobaltEcwAppointmentIdentity(group) ? EhrAppointmentIdNamespace.ECW : null;
}

/**
 * Read the canonical eCW encounter id from the successful Cobalt create webhook. The webhook is
 * already polled to verify booking, so using its payload avoids another Cobalt round trip. Match
 * the Cobalt appointment id before accepting the encounter id so an unexpected payload cannot be
 * attached to the wrong appointment.
 */
export function getCobaltCreateWebhookSourceEhrAppointmentId(
  data: unknown,
  expectedAppointmentId: string
): string | null {
  const parsed = cobaltAppointmentCreatedWebhookDataSchema.safeParse(data);

  if (!parsed.success || parsed.data.appointment_id !== expectedAppointmentId) {
    return null;
  }

  return parsed.data.emr_appointment_id;
}

function resolutionHints(appointmentDatetime: string | null): AppointmentTarget["resolutionHints"] {
  const parsed = isoDateTimeSchema.safeParse(appointmentDatetime);

  return parsed.success ? { appointmentDatetime: parsed.data } : undefined;
}

/**
 * Attach the canonical identifier emitted by a direct EHR scheduling adapter. This is additive:
 * callers that still consume `ehrId` see the same value, while newer callers can carry the
 * self-describing target through a later backend switch.
 */
export function withCanonicalAppointmentIdentity<T extends Appointment>(
  appointment: T,
  namespace: EhrAppointmentIdNamespace
): T {
  if (!appointment.ehrId) {
    throw new Error(`Cannot classify an appointment without an ehrId as ${namespace}`);
  }

  const hints = resolutionHints(appointment.appointmentDatetime);

  const appointmentTarget = appointmentTargetSchema.parse({
    version: 1,
    ehr: { namespace, id: appointment.ehrId },
    integration: null,
    ...(hints ? { resolutionHints: hints } : {}),
  });

  return {
    ...appointment,
    ehrIdNamespace: namespace,
    integrationAppointmentId: null,
    integrationMetadata: null,
    integrationNamespace: null,
    appointmentTarget,
  };
}

/**
 * Attach both identifiers emitted by Cobalt while preserving the legacy meaning of `ehrId`.
 * Until the action boundary is versioned, existing callers must continue receiving the Cobalt
 * operational id in `ehrId`; the canonical eCW encounter id is carried separately.
 */
export function withCobaltAppointmentIdentity<T extends Appointment>(
  appointment: T,
  sourceEhrAppointmentId: unknown,
  group: Group
): T {
  if (!supportsCobaltEcwAppointmentIdentity(group)) {
    return appointment;
  }

  if (!appointment.ehrId) {
    throw new Error("Cannot classify a Cobalt appointment without an ehrId");
  }

  const hints = resolutionHints(appointment.appointmentDatetime);
  const normalizedSourceEhrAppointmentId = normalizeCobaltAppointmentIdentifier(sourceEhrAppointmentId);

  const appointmentTarget = appointmentTargetSchema.parse({
    version: 1,
    ehr: normalizedSourceEhrAppointmentId
      ? { namespace: EhrAppointmentIdNamespace.ECW, id: normalizedSourceEhrAppointmentId }
      : null,
    integration: {
      namespace: AppointmentIntegrationNamespace.COBALT,
      id: appointment.ehrId,
    },
    ...(hints ? { resolutionHints: hints } : {}),
  });

  return {
    ...appointment,
    // `ehrId` still contains the Cobalt id for v0 compatibility, so do not misclassify it as eCW.
    ehrIdNamespace: null,
    integrationAppointmentId: appointment.ehrId,
    integrationMetadata: null,
    integrationNamespace: AppointmentIntegrationNamespace.COBALT,
    sourceEhrAppointmentId: normalizedSourceEhrAppointmentId,
    appointmentTarget,
  };
}

export class AppointmentIdentityConflictError extends Error {
  constructor(authority: "ehr" | "integration") {
    super(`Conflicting ${authority} appointment identifiers`);
    this.name = "AppointmentIdentityConflictError";
  }
}

/**
 * Preserve a known appointment namespace when an older EHR mapper emits null, enrich a legacy
 * row when the mapper knows the namespace, and fail closed if two authorities disagree.
 */
export function resolveEhrAppointmentIdNamespace(
  existing: EhrAppointmentIdNamespace | null,
  incoming: EhrAppointmentIdNamespace | null
): EhrAppointmentIdNamespace | null {
  if (existing !== null && incoming !== null && existing !== incoming) {
    throw new AppointmentIdentityConflictError("ehr");
  }

  return incoming ?? existing;
}

function uniqueIdentifier(authority: "ehr" | "integration", values: Array<string | null | undefined>): string | null {
  const identifiers = [...new Set(values.filter((value): value is string => Boolean(value)))];

  if (identifiers.length > 1) {
    throw new AppointmentIdentityConflictError(authority);
  }

  return identifiers[0] ?? null;
}

type PersistedAppointmentIdentity = Pick<
  Appointment,
  "ehrId" | "ehrIdNamespace" | "integrationAppointmentId" | "integrationNamespace"
>;

type RuntimeAppointmentIdentity = PersistedAppointmentIdentity &
  Pick<Appointment, "appointmentTarget" | "sourceEhrAppointmentId">;

export type CobaltAppointmentIdentityReconciliation = {
  /** A conflicting eCW mapping is quarantined from the runtime action target. */
  conflict: "ehr" | null;
  persistence: PersistedAppointmentIdentity;
  runtime: RuntimeAppointmentIdentity;
};

function identifiers(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

/**
 * Produce the two representations needed during the Cobalt-to-WebECW migration:
 *
 * - runtime keeps the Cobalt id in `ehrId` for v0 callers and carries both ids in the v1 target;
 * - persistence stores the eCW encounter id as `ehrId` when known and the Cobalt id separately.
 *
 * Existing identifiers are monotonic: missing runtime enrichment cannot erase them and newly
 * learned identifiers enrich the persisted row. Conflicting eCW ids are withheld from the action
 * target so a successful write remains reported as successful while WebECW actions fail closed.
 * Cobalt's operational id is never persisted in `ehrId`: when the eCW encounter id is unavailable,
 * the canonical columns stay null and the Cobalt id remains available in the integration columns.
 */
export function reconcileCobaltAppointmentIdentity(
  runtimeAppointment: Appointment,
  persistedAppointment?: Appointment
): CobaltAppointmentIdentityReconciliation | null {
  if (runtimeAppointment.appointmentTarget?.integration?.namespace !== AppointmentIntegrationNamespace.COBALT) {
    return null;
  }

  const persistedTarget = persistedAppointment?.appointmentTarget;
  const runtimeTarget = runtimeAppointment.appointmentTarget;
  const ehrIds = identifiers([
    persistedAppointment?.ehrIdNamespace === EhrAppointmentIdNamespace.ECW ? persistedAppointment.ehrId : null,
    persistedTarget?.ehr?.namespace === EhrAppointmentIdNamespace.ECW ? persistedTarget.ehr.id : null,
    runtimeAppointment.ehrIdNamespace === EhrAppointmentIdNamespace.ECW ? runtimeAppointment.ehrId : null,
    runtimeAppointment.sourceEhrAppointmentId,
    runtimeTarget.ehr?.namespace === EhrAppointmentIdNamespace.ECW ? runtimeTarget.ehr.id : null,
  ]);
  const ehrConflict = ehrIds.length > 1;
  const ehrId = ehrConflict ? null : (ehrIds[0] ?? null);
  const integrationId = uniqueIdentifier("integration", [
    persistedAppointment?.integrationNamespace === AppointmentIntegrationNamespace.COBALT
      ? persistedAppointment.integrationAppointmentId
      : null,
    persistedAppointment?.ehrIdNamespace == null ? persistedAppointment?.ehrId : null,
    persistedTarget?.integration?.namespace === AppointmentIntegrationNamespace.COBALT
      ? persistedTarget.integration.id
      : null,
    runtimeAppointment.integrationNamespace === AppointmentIntegrationNamespace.COBALT
      ? runtimeAppointment.integrationAppointmentId
      : null,
    runtimeAppointment.ehrIdNamespace == null ? runtimeAppointment.ehrId : null,
    runtimeTarget.integration?.id,
  ]);

  if (!integrationId) {
    throw new AppointmentIdentityConflictError("integration");
  }

  const appointmentTarget = appointmentTargetSchema.parse({
    version: 1,
    ...(runtimeTarget.valerieAppointmentId || persistedTarget?.valerieAppointmentId
      ? { valerieAppointmentId: runtimeTarget.valerieAppointmentId ?? persistedTarget?.valerieAppointmentId }
      : {}),
    ehr: ehrId ? { namespace: EhrAppointmentIdNamespace.ECW, id: ehrId } : null,
    integration: { namespace: AppointmentIntegrationNamespace.COBALT, id: integrationId },
    ...(runtimeTarget.resolutionHints || persistedTarget?.resolutionHints
      ? { resolutionHints: runtimeTarget.resolutionHints ?? persistedTarget?.resolutionHints }
      : {}),
  });

  const runtime: RuntimeAppointmentIdentity = {
    // Existing v0 callers still require the Cobalt operational id in `ehrId`.
    ehrId: integrationId,
    ehrIdNamespace: null,
    integrationAppointmentId: integrationId,
    integrationNamespace: AppointmentIntegrationNamespace.COBALT,
    sourceEhrAppointmentId: ehrId,
    appointmentTarget,
  };

  const persistedCanonicalEhrId =
    persistedAppointment?.ehrIdNamespace === EhrAppointmentIdNamespace.ECW ? persistedAppointment.ehrId : null;
  const persistenceEhrId = ehrConflict ? persistedCanonicalEhrId : ehrId;
  const persistence: PersistedAppointmentIdentity = {
    // Preserve only an explicitly namespaced source-EHR id. A legacy unnamespaced value from
    // Cobalt is represented by the integration columns and must not leak back into canonical EHR
    // storage during a Cobalt rollback.
    ehrId: persistenceEhrId,
    ehrIdNamespace: persistenceEhrId ? EhrAppointmentIdNamespace.ECW : null,
    integrationAppointmentId: integrationId,
    integrationNamespace: AppointmentIntegrationNamespace.COBALT,
  };

  return {
    conflict: ehrConflict ? "ehr" : null,
    persistence,
    runtime,
  };
}
